import { useEffect, useMemo } from 'react'

import { FormProvider, useForm } from 'react-hook-form'

import { SsoStatus, SsoType } from '@tribeplatform/gql-client/types'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  useNetwork,
  useUpdateNetwork,
  useSsos,
} from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { SettingsLayout } from '../../Layout/SettingsLayout.js'

interface EmailAuthFormFields {
  email: boolean
}

export function EmailAuth() {
  const { $t } = useI18n()
  const { data: network } = useNetwork()
  const { mutateAsync: updateNetwork, isLoading } = useUpdateNetwork()
  const { data: ssos } = useSsos()

  const defaultValues = useMemo(
    () => ({
      email: !network?.hideDefaultAuthenticationForm,
    }),
    [network?.hideDefaultAuthenticationForm],
  )

  const form = useForm<EmailAuthFormFields>({
    defaultValues,
  })

  useEffect(() => {
    form.reset(defaultValues)
  }, [defaultValues, form])

  const enabledSsosCount = useMemo(
    () =>
      ssos?.reduce((sum, sso) => {
        if (sso.type === SsoType.jwt) {
          if (
            sso.status === SsoStatus.enable &&
            (sso.authorizationUrl || sso.signUpUrl)
          ) {
            sum += 1
          }
          return sum
        }
        if (sso.status === SsoStatus.enable) {
          sum += 1
        }
        return sum
      }, 0),
    [ssos],
  )

  const activeLoginProviderCount =
    enabledSsosCount + (network?.hideDefaultAuthenticationForm ? 0 : 1)
  const canSafelyDisableLoginProvider = activeLoginProviderCount > 1

  const onSubmit = async (data: EmailAuthFormFields) => {
    try {
      await updateNetwork({
        input: { hideDefaultAuthenticationForm: !data.email },
      })
      toast({
        title: $t({
          defaultMessage: 'Email authentication settings updated',
          id: 'Admin.Auth.EmailLogin.Updated',
        }),
        status: 'success',
      })
      form.reset(data)
    } catch {
      toast({
        title: $t({
          defaultMessage: 'Unable to update email authentication settings',
          id: 'Admin.Auth.EmailLogin.FailedToUpdate',
        }),
        status: 'error',
      })
    }
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <SettingsLayout
          title={$t({ defaultMessage: 'Email', id: 'Generics.Email' })}
          subtitle={$t({
            defaultMessage:
              'Configure email signup and login settings for your community.',
            id: 'Admin.Auth.EmailLogin.Header.Description',
          })}
          disabled={!form.formState.isDirty}
          isLoading={isLoading}
          onCancel={() => form.reset()}
          saveLabel={<T defaultMessage="Update" id="Generics.Update" />}
        >
          <div className="space-y-6">
            <Alert
              status="info"
              title={$t({
                defaultMessage: 'Email authentication enabled',
                id: 'Admin.Auth.EmailLogin.Alert.Title',
              })}
            >
              <T
                defaultMessage="Members can sign up and log in using their email address. A verification code will be sent to confirm their identity."
                id="Admin.Auth.EmailLogin.Alert.Description"
              />
            </Alert>
            <Form.Toggle
              name="email"
              label={$t({
                defaultMessage: 'Login & register with email',
                id: 'Admin.Auth.EmailLogin',
              })}
              helperText={$t({
                defaultMessage:
                  'Allow users to register and access the community with an email and password.',
                id: 'Admin.Auth.EmailLogin.Description',
              })}
              disabled={
                !canSafelyDisableLoginProvider &&
                !network?.hideDefaultAuthenticationForm
              }
            />
          </div>
        </SettingsLayout>
      </form>
    </FormProvider>
  )
}
